/**
 * Route gate for the Proposal Builder V2 (docs/proposal-v2/S2a.md §3.4,
 * S10.md §3.1).
 *
 * The launcher rule lives in ONE place, `useProposalBuilderV2Allowed`: the
 * tenant has `featureFlags.proposalBuilderV2` on, or the signed-in user is
 * an admin (`isAdminSync` — the cached `isAdmin` claim, fail-closed). The
 * gate, the deal's proposal card and the old builder's header all read it.
 *
 * The gate waits for the tenant doc before deciding, so a flagged tenant
 * never sees a flash of the denial. A deal already on the new builder
 * (`proposalBuilderVersion === 2`) is let in whatever the rule says
 * (`opensInV2`): it can only be edited here — the old builder is read-only
 * for it — so turning a user away would strand the deal. That one read of
 * the deal happens only when the rule says no. Hiding a launcher is not
 * gating: deep links land here too.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { RouteFallback } from '@/components/RouteFallback';
import { useTenant } from '@/hooks/useTenant';
import { getDeal } from '@/services/deal.service';
import { isAdminSync } from '@/utils/access';
import { opensInV2 } from './launcher';

/**
 * The launcher rule (S10 §3.1): the tenant flag is on, or the user is an
 * admin. `allowed` is false while the tenant doc loads (`loading`) — the
 * flag reads false then, as `useFeatureFlag` does.
 */
export function useProposalBuilderV2Allowed(): { allowed: boolean; loading: boolean } {
  const { tenant, isLoading } = useTenant();
  if (isLoading) return { allowed: false, loading: true };
  return { allowed: tenant?.featureFlags?.proposalBuilderV2 === true || isAdminSync(), loading: false };
}

/** Is the deal on the new builder? `null` while reading; only reads when `enabled`. */
function useDealOnV2(dealId: string | undefined, enabled: boolean): boolean | null {
  const [onV2, setOnV2] = useState<{ dealId: string; v2: boolean } | null>(null);
  useEffect(() => {
    if (!enabled || !dealId) return undefined;
    let live = true;
    const settle = (v2: boolean) => {
      if (live) setOnV2({ dealId, v2 });
    };
    getDeal(dealId)
      .then((deal) => settle(deal ? opensInV2(deal, false) : false))
      .catch(() => settle(false));
    return () => {
      live = false;
    };
  }, [dealId, enabled]);
  if (!enabled || !dealId) return false;
  return onV2?.dealId === dealId ? onV2.v2 : null;
}

export function RequireProposalBuilderV2({ children }: { children: ReactNode }) {
  const { allowed, loading } = useProposalBuilderV2Allowed();
  const { dealId } = useParams();
  const dealOnV2 = useDealOnV2(dealId, !loading && !allowed);

  if (loading || dealOnV2 === null) return <RouteFallback />;
  if (allowed || dealOnV2) return <>{children}</>;

  return (
    <div className="min-h-[60vh] bg-surface flex items-center justify-center p-6" data-gate="proposal-builder-v2">
      <div className="text-center max-w-sm">
        <Lock className="w-12 h-12 text-theme-muted mx-auto mb-3" aria-hidden="true" />
        <h2 className="text-lg font-bold text-theme-primary">Not switched on yet</h2>
        <p className="text-sm text-theme-muted mt-1">
          The new proposal builder isn&apos;t switched on for your team yet.
        </p>
        <Link
          to={dealId ? `/home/deals/${dealId}` : '/home/deals'}
          className="inline-block mt-4 px-5 py-2 rounded-full bg-surface-hover text-sm text-theme-primary transition-colors"
        >
          {dealId ? 'Back to the deal' : 'Back to deals'}
        </Link>
      </div>
    </div>
  );
}
